// Immobilienmappen — der einzige Sachabschnitt des Risiken-Reiters, wenn der
// ganze Unternehmensverbund in der Immobilienwirtschaft steht.
//
// Aufbau je Gebäude nach der Vorlage vom 25.08.2026: zentriert und groß, weil
// hier NICHT zwanzig Positionen zu überfliegen sind, sondern eine Handvoll
// Objekte einzeln betrachtet werden. Von oben nach unten —
//
//   Kategorie („Wohnimmobilie") → Typ → Bild → TIM-online / Google Earth →
//   Anschrift → Eigentümer (nur im Verbund) → Verwalter →
//   Wert der Immobilie, Einheiten, Fläche.
//
// Aufgeklappt („Weitere Details") folgen das Risiko selbst, dann Verträge,
// Schäden und Vorgänge (`ImmobilienMappeDetails`).
//
// Farben: die blaue Skala (`immoTokens`), nicht das Aubergine des übrigen
// Reiters — die Immobilienansicht ist eine eigene Gliederung derselben Seite.

import { useEffect, useMemo, useState } from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { apiFetch } from "../../utils/apiFetch";
import { IMMO } from "./immoTokens";
import type { Verwaltung } from "../redesign/OrganigrammPopup";
import { Icons } from "./riskIcons";
import { ImmobilienMappeDetails } from "./ImmobilienMappeDetails";
import {
  charOf,
  contactLabel,
  eigentuemerVon,
  eur,
  istGebaeude,
  resolveSignedUrl,
  type MappeRisk,
  type OpenCounts,
} from "./mappeDaten";
import {
  buildGoogleEarthUrl,
  buildTimOnlineUrl,
  formatFullAddress,
  timOnlineMoeglich,
  buildHochwasserCheckUrl,
  hochwasserCheckMoeglich,
  HOCHWASSER_HINWEIS,
  type AddressParts,
} from "../../utils/geoLinks";

/* ── Ableitungen aus den Merkmalen ──────────────────────────────────────── */

/**
 * Überschrift der Mappe: die Nutzungskategorie als Wort, nicht als Katalogwert.
 * Die Vorlage verlangt „Wohn/Gewerbe oder beides — Immobilie"; „Wohnen (100%)"
 * ist der Speicherwert des Fragebogens (`risikoImmobilieStep/enums.ts`) und
 * liest sich als Überschrift wie ein Formularfeld.
 */
export const kategorieUeberschrift = (kategorie: string | null): string => {
  const k = (kategorie ?? "").toLowerCase();
  if (!k) return "Immobilie";
  const wohnen = k.includes("wohn");
  const gewerbe = k.includes("gewerbe");
  if (wohnen && gewerbe) return "Wohn- und Gewerbeimmobilie";
  if (wohnen) return "Wohnimmobilie";
  if (gewerbe) return "Gewerbeimmobilie";
  return "Immobilie";
};

/** Anschriftsteile des Gebäudes — Quelle für Anzeige UND Kartendienste. */
const adresseVon = (risk: MappeRisk): AddressParts => ({
  street: charOf(risk, "street", "Straße"),
  postalCode: charOf(risk, "postalCode", "PLZ"),
  city: charOf(risk, "city", "Ort"),
  country: charOf(risk, "country", "Land"),
});

/**
 * Beauftragte Verwaltung des Gebäudes.
 *
 * Der Fragebogen legt sie zweifach ab: `hvKontaktRef` als Kontaktbezug
 * (`{ contactId, label }`) und `hvName` als Klartext. Für das Organigramm zählt
 * nur der Kontaktbezug — ohne ihn gäbe es kein Kästchen, das irgendwohin führt.
 */
export const hausverwaltungVon = (risk: MappeRisk): Verwaltung | null => {
  const roh = charOf(risk, "hvKontaktRef");
  const name = charOf(risk, "hvName");
  if (roh) {
    try {
      const o = JSON.parse(roh) as Record<string, unknown>;
      const id = Number(o?.contactId);
      if (Number.isFinite(id) && id > 0) {
        return { contactId: id, name: String(o.label ?? name ?? "").trim() || `Kontakt ${id}` };
      }
    } catch {
      /* Freitext statt Objekt — dann bleibt nur der Name, siehe unten. */
    }
  }
  return null;
};

/**
 * Name der Verwaltung für die Anzeige: der Kontaktbezug, ersatzweise der
 * Klartext `hvName` — anders als im Organigramm zählt hier auch ein Name ohne
 * Kontakt.
 */
const verwalterName = (risk: MappeRisk): string | null =>
  hausverwaltungVon(risk)?.name ?? charOf(risk, "hvName");

/** Zahl aus einem Merkmal — deutsch („1.250,5") oder technisch („1250.5"). */
const zahlAus = (roh: unknown): number | null => {
  if (typeof roh === "number") return Number.isFinite(roh) && roh > 0 ? roh : null;
  const t = String(roh ?? "").trim();
  if (!t) return null;
  const n = Number(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Die Einheitenliste des Fragebogens (`einheiten`, JSON-Array im Merkmal). */
const einheitenListe = (risk: MappeRisk): Record<string, unknown>[] => {
  const roh = charOf(risk, "einheiten");
  if (!roh) return [];
  try {
    const p = JSON.parse(roh) as unknown;
    return Array.isArray(p) ? (p.filter((e) => e && typeof e === "object") as Record<string, unknown>[]) : [];
  } catch {
    return [];
  }
};

/** Anzahl der Einheiten: die erfasste Zahl, ersatzweise die Länge der Liste. */
export const einheitenVon = (risk: MappeRisk): number | null =>
  zahlAus(charOf(risk, "anzahlEinheiten")) ?? (einheitenListe(risk).length || null);

/**
 * Fläche in m²: die gespeicherte Gesamtfläche (`gesamtflaeche`, Altbestand
 * `flaecheGesamt`), ersatzweise die Summe der Einheitenflächen — genau die
 * Größe, aus der der Fragebogen die Gesamtfläche vorschlägt.
 */
export const flaecheVon = (risk: MappeRisk): number | null => {
  const gesamt = zahlAus(charOf(risk, "gesamtflaeche", "flaecheGesamt"));
  if (gesamt != null) return gesamt;
  const summe = einheitenListe(risk).reduce((acc, e) => acc + (zahlAus(e.flaeche) ?? 0), 0);
  return summe > 0 ? summe : null;
};

const qm = (v: number) => `${v.toLocaleString("de-DE", { maximumFractionDigits: 1 })} m²`;

/** Wert der Immobilie: Versicherungssumme, ersatzweise der erfasste Neuwert. */
const wertVon = (risk: MappeRisk): number | null => {
  const vs = Number(risk.versicherungssumme);
  if (Number.isFinite(vs) && vs > 0) return vs;
  const neuwert = Number(
    (charOf(risk, "Neuwert", "neuwert") ?? "").replace(/\./g, "").replace(",", "."),
  );
  return Number.isFinite(neuwert) && neuwert > 0 ? neuwert : null;
};

/* ── Styles ─────────────────────────────────────────────────────────────── */

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "16px" },

  karte: {
    backgroundColor: IMMO.white,
    position: "relative",
    ...shorthands.overflow("hidden"),
    ...shorthands.border("1px", "solid", IMMO.border),
    ...shorthands.borderRadius("14px"),
  },
  karteOffen: { ...shorthands.borderColor(IMMO.blau) },
  karteOhneVertrag: { ...shorthands.borderColor("#e6c3ba") },

  kopf: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "6px",
    paddingTop: "20px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    textAlign: "center",
  },
  kategorie: {
    fontFamily: IMMO.fontAccent,
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.15,
    color: IMMO.ink,
  },
  typ: {
    fontFamily: IMMO.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: IMMO.blau,
  },
  typLeer: { fontSize: "0.86rem", color: IMMO.muted, fontStyle: "italic" },

  bild: {
    width: "min(340px, 100%)",
    height: "170px",
    marginTop: "10px",
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.overflow("hidden"),
    backgroundColor: IMMO.bgSoft,
    ...shorthands.border("1px", "solid", IMMO.border),
    ...shorthands.borderRadius("12px"),
  },
  bildImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  bildIcon: { width: "62px", height: "62px", color: IMMO.blau, opacity: 0.5, display: "inline-flex" },

  geoZeile: { display: "flex", columnGap: "8px", flexWrap: "wrap", justifyContent: "center" },
  geoBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    textDecorationLine: "none",
    fontFamily: IMMO.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    color: IMMO.blau,
    backgroundColor: IMMO.white,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "13px",
    ...shorthands.border("1.5px", "solid", IMMO.border),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: IMMO.bgSelect, ...shorthands.borderColor(IMMO.blau) },
  },
  geoBtnAus: { opacity: 0.45, pointerEvents: "none" },
  geoIcon: { width: "14px", height: "14px", display: "inline-flex" },

  anschrift: {
    marginTop: "10px",
    fontFamily: IMMO.fontPrimary,
    fontSize: "1rem",
    fontWeight: 600,
    color: IMMO.ink,
  },
  anschriftLeer: { marginTop: "10px", fontSize: "0.88rem", color: IMMO.muted, fontStyle: "italic" },

  paare: {
    display: "flex",
    flexDirection: "column",
    rowGap: "3px",
    marginTop: "8px",
    fontSize: "0.86rem",
    color: IMMO.ink,
  },
  paarKey: { color: IMMO.muted, fontWeight: 600, marginRight: "5px" },

  wertZeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "18px",
    rowGap: "8px",
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: "14px",
    width: "100%",
  },
  wert: {
    fontFamily: IMMO.fontAccent,
    fontSize: "1.4rem",
    fontWeight: 700,
    color: IMMO.ink,
    fontVariantNumeric: "tabular-nums",
  },
  wertLabel: { fontSize: "0.72rem", color: IMMO.muted, fontWeight: 600, display: "block" },
  wertLeer: { fontSize: "0.95rem", color: IMMO.muted, fontStyle: "italic" },
  /** Wert, Einheiten und Fläche nebeneinander — je eine Kennzahl. */
  kennzahl: { display: "flex", flexDirection: "column", alignItems: "center", minWidth: "110px" },
  kennzahlTrenner: {
    alignSelf: "stretch",
    width: "1px",
    backgroundColor: IMMO.border,
  },

  badges: { display: "flex", columnGap: "6px", rowGap: "6px", flexWrap: "wrap", justifyContent: "center" },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    fontSize: "0.72rem",
    fontWeight: 600,
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.borderRadius("999px"),
  },
  badgeOk: { backgroundColor: IMMO.okBg, color: IMMO.okInk },
  badgeWarn: { backgroundColor: IMMO.warnBg, color: IMMO.warnInk },

  /** Aufklapper als volle Fußzeile — die Karte selbst ist kein Knopf, weil in
      ihr Links (Karten, Risiko) sitzen und ein Knopf im Knopf ungültig wäre. */
  klappBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    columnGap: "8px",
    width: "100%",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: IMMO.cardBg,
    color: IMMO.blau,
    fontFamily: IMMO.fontPrimary,
    fontSize: "0.8rem",
    fontWeight: 700,
    paddingTop: "9px",
    paddingBottom: "9px",
    ...shorthands.border("0"),
    ...shorthands.borderTop("1px", "solid", IMMO.border),
    ":hover": { backgroundColor: IMMO.bgSelect },
  },
  chevron: {
    width: "14px",
    height: "14px",
    display: "inline-flex",
    transitionProperty: "transform",
    transitionDuration: "140ms",
  },
  chevronOffen: { transform: "rotate(90deg)" },

  leer: { fontSize: "0.85rem", color: IMMO.muted, fontStyle: "italic" },
});

/* ── Kartendienst-Zeichen (klein, im Stil der Anschrift-Karte) ──────────── */

const EarthIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <circle cx="8" cy="8" r="6.6" />
    <path d="M1.4 8h13.2M8 1.4c2 2.4 3 5 3 6.6s-1 4.2-3 6.6c-2-2.4-3-5-3-6.6s1-4.2 3-6.6z" />
  </svg>
);

const NrwIcon = () => (
  <svg viewBox="0 0 16 18" fill="none" stroke="currentColor" strokeWidth="1.3">
    <path d="M2 2h12v8c0 4-3 6-6 7-3-1-6-3-6-7V2z" />
    <path d="M5.5 7v4M10.5 7v4M5.5 9h5" />
  </svg>
);

/** Tropfen über einer Welle — Hochwasser-Check (T-339). */
const FlutIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <path d="M8 1.6C6 4.4 4.6 6.3 4.6 8.1a3.4 3.4 0 0 0 6.8 0C11.4 6.3 10 4.4 8 1.6z" />
    <path d="M1.4 14c1.1 0 1.6-.8 2.2-.8s1.1.8 2.2.8 1.6-.8 2.2-.8 1.1.8 2.2.8 1.6-.8 2.2-.8 1.1.8 2.2.8" />
  </svg>
);

/* ── Komponente ─────────────────────────────────────────────────────────── */

export function ImmobilienmappenSection({
  selectedIds,
  verbundAnsicht,
  onBefund,
  onVerwaltungen,
}: {
  selectedIds: number[];
  /** Im Verbund gehört zu jedem Gebäude, WEM es gehört — bei einer Firma nicht. */
  verbundAnsicht: boolean;
  /** Zahl der Gebäude nach oben melden — steuert den Startzustand des Abschnitts. */
  onBefund?: (anzahl: number) => void;
  /** Beauftragte Verwaltungen der Gebäude — das Organigramm zeigt sie mit an. */
  onVerwaltungen?: (verwaltungen: Verwaltung[]) => void;
}) {
  const styles = useStyles();

  /** Genau eine offene Mappe — die Detaildaten werden je Mappe nachgeladen. */
  const [offen, setOffen] = useState<number | null>(null);
  const [risks, setRisks] = useState<MappeRisk[]>([]);
  const [thumbs, setThumbs] = useState<Record<number, string>>({});
  const [counts, setCounts] = useState<OpenCounts>({});
  const [loading, setLoading] = useState(false);

  const idsKey = selectedIds.join(",");

  useEffect(() => {
    if (!idsKey) {
      setRisks([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiFetch<MappeRisk[]>(`risk-objects/by-contacts`, { params: { ids: idsKey } })
      .then(({ data }) => {
        if (!cancelled) setRisks(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRisks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  const gebaeude = useMemo(() => risks.filter(istGebaeude), [risks]);

  const objektIds = useMemo(
    () => gebaeude.map((r) => r.id).sort((a, b) => a - b),
    [gebaeude],
  );
  const objektIdsKey = objektIds.join(",");

  useEffect(() => {
    onBefund?.(objektIds.length);
  }, [objektIds.length, onBefund]);

  // Die Verwaltungen kommen aus den Gebäuden, nicht aus den Kontaktbeziehungen:
  // Beauftragt wird je Objekt, und genau dort ist es erfasst (`hvKontaktRef`).
  const verwaltungen = useMemo(() => {
    const nachId = new Map<number, Verwaltung>();
    for (const g of gebaeude) {
      const v = hausverwaltungVon(g);
      if (v && !nachId.has(v.contactId)) nachId.set(v.contactId, v);
    }
    return Array.from(nachId.values()).sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [gebaeude]);
  // Über den Schlüssel gemeldet, nicht über die Liste: `verwaltungen` ist bei
  // jedem Laden ein neues Array, und der Empfänger hängt es in einen Memo —
  // eine neue Referenz je Render ließe das Organigramm endlos neu rechnen.
  const verwaltungenKey = verwaltungen.map((v) => `${v.contactId}:${v.name}`).join("|");
  useEffect(() => {
    onVerwaltungen?.(
      verwaltungenKey
        ? verwaltungenKey.split("|").map((t) => {
            const trenn = t.indexOf(":");
            return { contactId: Number(t.slice(0, trenn)), name: t.slice(trenn + 1) };
          })
        : [],
    );
  }, [verwaltungenKey, onVerwaltungen]);

  useEffect(() => {
    if (!objektIdsKey) {
      setThumbs({});
      setCounts({});
      return;
    }
    let cancelled = false;
    apiFetch<Record<number, string>>(`risk-objects/thumbs`, { params: { ids: objektIdsKey } })
      .then(({ data }) => {
        if (cancelled) return;
        const aufgeloest: Record<number, string> = {};
        for (const [riskId, url] of Object.entries(data ?? {})) {
          aufgeloest[Number(riskId)] = resolveSignedUrl(url);
        }
        setThumbs(aufgeloest);
      })
      .catch(() => {
        if (!cancelled) setThumbs({});
      });
    apiFetch<OpenCounts>(`risk-objects/open-counts`, { params: { ids: objektIdsKey } })
      .then(({ data }) => {
        if (!cancelled) setCounts(data ?? {});
      })
      .catch(() => {
        if (!cancelled) setCounts({});
      });
    return () => {
      cancelled = true;
    };
  }, [objektIdsKey]);

  if (loading) return <div className={styles.leer}>Immobilien werden geladen …</div>;
  if (gebaeude.length === 0)
    return <div className={styles.leer}>Für diese Auswahl ist kein Gebäude erfasst.</div>;

  return (
    <div className={styles.root}>
      {gebaeude.map((risk) => {
        const adresse = adresseVon(risk);
        const anschrift = formatFullAddress({ ...adresse, country: null });
        const hatAdresse = anschrift.trim().length > 0;
        const typ = charOf(risk, "typ");
        const eigentuemer = eigentuemerVon(risk);
        const verwalter = verwalterName(risk);
        const wert = wertVon(risk);
        const einheiten = einheitenVon(risk);
        const flaeche = flaecheVon(risk);
        const versichert = !!(risk.contract || risk.contractID);
        const c = counts[risk.id];
        const istOffen = offen === risk.id;
        const bild = thumbs[risk.id];
        // Der "#"-Zweig hält Mittelklick / „in neuem Tab" davon ab, eine leere
        // Such-URL bzw. ein „?adresse=," zu öffnen (siehe geoLinks).
        const earthHref = hatAdresse
          ? buildGoogleEarthUrl(formatFullAddress(adresse))
          : "#";
        const timHref = hatAdresse ? buildTimOnlineUrl(adresse) : "#";
        const hochwasserHref = hatAdresse ? buildHochwasserCheckUrl(adresse) : "#";

        return (
          <div
            key={risk.id}
            className={mergeClasses(
              styles.karte,
              istOffen && styles.karteOffen,
              !versichert && styles.karteOhneVertrag,
            )}
          >
            <div className={styles.kopf}>
              <span className={styles.kategorie}>
                {kategorieUeberschrift(charOf(risk, "kategorie"))}
              </span>
              <span className={typ ? styles.typ : styles.typLeer}>
                {typ ?? "Typ nicht erfasst"}
              </span>

              <div className={styles.bild}>
                {bild ? (
                  <img className={styles.bildImg} src={bild} alt={anschrift || "Immobilie"} />
                ) : (
                  <span className={styles.bildIcon} aria-hidden>
                    <Icons.house />
                  </span>
                )}
              </div>

              <div className={styles.geoZeile}>
                {timOnlineMoeglich(adresse) && (
                  <a
                    className={mergeClasses(styles.geoBtn, !hatAdresse && styles.geoBtnAus)}
                    href={timHref}
                    target={hatAdresse ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    aria-disabled={!hatAdresse}
                    onClick={(e) => {
                      if (!hatAdresse) e.preventDefault();
                    }}
                  >
                    <span className={styles.geoIcon}>
                      <NrwIcon />
                    </span>
                    TIM-online
                  </a>
                )}
                {/* T-339: GDV-Hochwasser-Check, nur verlinkt (Einbetten ist
                    Maklern nicht erlaubt). Der Hinweis zur ZÜRS-Abfrage steht
                    im Tooltip. */}
                {hochwasserCheckMoeglich(adresse) && (
                  <a
                    className={mergeClasses(styles.geoBtn, !hatAdresse && styles.geoBtnAus)}
                    href={hochwasserHref}
                    target={hatAdresse ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    aria-disabled={!hatAdresse}
                    title={HOCHWASSER_HINWEIS}
                    onClick={(e) => {
                      if (!hatAdresse) e.preventDefault();
                    }}
                  >
                    <span className={styles.geoIcon}>
                      <FlutIcon />
                    </span>
                    Hochwasser-Check
                  </a>
                )}
                <a
                  className={mergeClasses(styles.geoBtn, !hatAdresse && styles.geoBtnAus)}
                  href={earthHref}
                  target={hatAdresse ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  aria-disabled={!hatAdresse}
                  onClick={(e) => {
                    if (!hatAdresse) e.preventDefault();
                  }}
                >
                  <span className={styles.geoIcon}>
                    <EarthIcon />
                  </span>
                  Google Earth
                </a>
              </div>

              <span className={hatAdresse ? styles.anschrift : styles.anschriftLeer}>
                {hatAdresse ? anschrift : "Anschrift nicht erfasst"}
              </span>

              <div className={styles.paare}>
                {/* Eigentümer nur im Verbund: Betrachtet man EINE Firma, gehört
                    ihr per Definition jedes Gebäude der Liste. */}
                {verbundAnsicht && (
                  <span>
                    <span className={styles.paarKey}>Eigentümer</span>
                    {contactLabel(eigentuemer)}
                  </span>
                )}
                <span>
                  {verwalter ? (
                    <>
                      <span className={styles.paarKey}>Verwalter</span>
                      {verwalter}
                    </>
                  ) : (
                    <span className={styles.paarKey}>Kein Verwalter</span>
                  )}
                </span>
              </div>

              <div className={styles.badges}>
                {versichert ? (
                  <span className={mergeClasses(styles.badge, styles.badgeOk)}>
                    ✓ {risk.contract?.branch ?? "Vertrag"}
                    {risk.contract?.contractNbr ? ` · ${risk.contract.contractNbr}` : ""}
                  </span>
                ) : (
                  <span className={mergeClasses(styles.badge, styles.badgeWarn)}>
                    ⚠ ohne Vertrag
                  </span>
                )}
                {!!c?.openSchaeden && (
                  <span className={mergeClasses(styles.badge, styles.badgeWarn)}>
                    {c.openSchaeden} offene{c.openSchaeden === 1 ? "r" : ""} Schaden
                    {c.openSchaeden === 1 ? "" : "sfälle"}
                  </span>
                )}
                {!!c?.openVorgaenge && (
                  <span className={mergeClasses(styles.badge, styles.badgeOk)}>
                    {c.openVorgaenge} offene{c.openVorgaenge === 1 ? "r" : ""} Vorgang
                    {c.openVorgaenge === 1 ? "" : "e"}
                  </span>
                )}
              </div>

              <div className={styles.wertZeile}>
                <span className={styles.kennzahl}>
                  <span className={styles.wertLabel}>Wert der Immobilie</span>
                  {wert != null ? (
                    <span className={styles.wert}>{eur(wert)}</span>
                  ) : (
                    <span className={styles.wertLeer}>nicht erfasst</span>
                  )}
                </span>
                <span className={styles.kennzahlTrenner} aria-hidden />
                <span className={styles.kennzahl}>
                  <span className={styles.wertLabel}>Einheiten</span>
                  {einheiten != null ? (
                    <span className={styles.wert}>{einheiten.toLocaleString("de-DE")}</span>
                  ) : (
                    <span className={styles.wertLeer}>nicht erfasst</span>
                  )}
                </span>
                <span className={styles.kennzahlTrenner} aria-hidden />
                <span className={styles.kennzahl}>
                  <span className={styles.wertLabel}>Fläche</span>
                  {flaeche != null ? (
                    <span className={styles.wert}>{qm(flaeche)}</span>
                  ) : (
                    <span className={styles.wertLeer}>nicht erfasst</span>
                  )}
                </span>
              </div>
            </div>

            <button
              type="button"
              className={styles.klappBtn}
              aria-expanded={istOffen}
              onClick={() => setOffen((prev) => (prev === risk.id ? null : risk.id))}
            >
              <span
                className={mergeClasses(styles.chevron, istOffen && styles.chevronOffen)}
                aria-hidden
              >
                <Icons.arrow />
              </span>
              {istOffen ? "Details zuklappen" : "Weitere Details"}
            </button>

            {istOffen && (
              <ImmobilienMappeDetails risk={risk} versicherungssumme={wert} />
            )}
          </div>
        );
      })}
    </div>
  );
}
